import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, CalendarDays, Wallet, BarChart3, CalendarHeart, GraduationCap,
  Cake, Tag, LogOut, Menu, X, ChevronDown, User, UserCog,
  Plus, MoreHorizontal, UserPlus, DollarSign, Hourglass, Banknote, ShoppingBag, CreditCard,
  HelpCircle, Building2, Ticket, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import HalloweenToggle from './HalloweenToggle';
import GlobalSearch from './GlobalSearch';
import { useIdleScreenSaver } from './useIdleScreenSaver';
import MyProfileModal from './MyProfileModal';
import { getMyProfile } from '../services/profileApi';
import { getInitials } from '../utils/userDisplay';
import ImpersonationBanner from './ImpersonationBanner';
import PlanBanner from './PlanBanner';
import { recordNav } from '../utils/navHistory';
import { registrarVista } from '../services/telemetry';
import { getClientRequestsCount, CLIENT_REQUESTS_EVENT } from '../services/clientRequestsApi';
import logo from '../assets/logo-badge.webp';

// El modo pantalla se descarga solo cuando se activa (idle o botón)
const ScreenSaverMode = lazy(() => import('./ScreenSaverMode'));
// Inactividad antes de mostrar el modo pantalla (2 min). Ajustable.
const SCREENSAVER_IDLE_MS = 120000;

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador', teacher: 'Profesor', student: 'Alumno',
};

// 12-oct-2026 (Carlos, ya lo había pedido varias veces): TODO el panel va a lo ancho (1600).
// Antes era una lista de rutas anchas y cada pantalla nueva nacía angosta (peticiones, ayuda,
// whatsapp…). Ahora es al revés: ancho por defecto y solo estas excepciones van a max-w-6xl
// (las que ya eran angostas: formularios/flujos de una columna y el recibo imprimible).
// 🚨 No agregues rutas aquí sin que Carlos lo pida, y no metas max-w propio dentro de las páginas.
const NARROW_ROUTES = new Set(['/prospectos', '/clases/clonar', '/mensajes']);
// 26-oct-2026: la ficha de EVENTO pasa a lo ancho (Finanzas del festival trae tablas); solo el recibo sigue angosto.
const isDense = (path: string) => !(NARROW_ROUTES.has(path) || /^\/recibo\/[^/]+$/.test(path));

// `mod` = módulo que gatea el item (si está inactivo en el tenant, el item se oculta).
// Items SIN `mod` (Inicio) se muestran siempre.
// `adminOnly` = solo dueña/gerente (user.role 'admin'), para lo que el módulo NO distingue: p.ej.
// `whatsapp` lo tiene staff=manage por defecto (lo necesita la recepción de la agenda dental).
type NavItem = { to: string; label: string; icon: typeof Users; ready?: boolean; mod?: string; mods?: string[]; adminOnly?: boolean };
type NavSection = { title: string; items: NavItem[] };
const SECTIONS: NavSection[] = [
  { title: 'Principal', items: [
    { to: '/', label: 'Inicio', icon: LayoutDashboard },
    // Caja como pantalla principal (como en la agenda dental). Desde 29-sep-2026 basta `cashbox`:
    // la cajera sin Finanzas entra aquí (solo lo del día); staff en t227 lleva cashbox=none.
    { to: '/caja', label: 'Caja', icon: Banknote, mod: 'cashbox' },
  ] },
  { title: 'Gestión', items: [
    { to: '/alumnos', label: 'Alumnos', icon: Users, mod: 'letsmove_members' },
    { to: '/profesores', label: 'Profesores', icon: GraduationCap, mod: 'letsmove_teachers' },
    { to: '/clases', label: 'Clases', icon: CalendarDays, mod: 'letsmove_classes' },
    // Prospectos OCULTO del menú (pedido Karla 1-oct en el grupo: «quitar prospectos de la barra
    // de la izq.»). La lista de espera sigue en cada clase y los prospectos en Alumnos → Prospectos;
    // la ruta /prospectos sigue viva por URL. Para revivirlo basta descomentar.
    // { to: '/prospectos', label: 'Prospectos', icon: Hourglass, mod: 'letsmove_classes' },
    { to: '/cumpleanos', label: 'Cumpleaños', icon: Cake, mod: 'birthdays' },
    // Mensajes OCULTO del menú (pedido Carlos 28-jul: «no lo usaremos de momento»).
    // La ruta /mensajes sigue viva por URL; para revivirlo basta descomentar.
    // { to: '/mensajes', label: 'Mensajes', icon: MessageCircle, mod: 'letsmove_messages' },
  ] },
  { title: 'Negocio', items: [
    // Finanzas · Reportes · Eventos seguidos (Mabel 31-oct-2026: es su trío de trabajo); Vitrina y Mensualidades después.
    { to: '/finanzas', label: 'Finanzas', icon: Wallet, mod: 'letsmove_finances' },
    { to: '/reportes', label: 'Reportes', icon: BarChart3, mod: 'letsmove_reports' },
    { to: '/eventos', label: 'Eventos', icon: CalendarHeart, mod: 'letsmove_events' },
    { to: '/vitrina', label: 'Vitrina', icon: ShoppingBag, mod: 'vitrina' },
    { to: '/membresias', label: 'Mensualidades', icon: Tag, mod: 'letsmove_memberships' },
    { to: '/usuarios', label: 'Usuarios', icon: UserCog, mod: 'users_admin' },
    // Mi plan (fase C, 18-ago-2026): suscripción y pago. El módulo 'billing' solo lo
    // tiene el owner (manager/staff=none) → el item se oculta solo para los demás.
    { to: '/mi-plan', label: 'Mi plan', icon: CreditCard, mod: 'billing' },
    // Mi Estudio (F1, 17-sep-2026): las reglas del negocio que antes solo vivían en el código.
    // Gate 'letsmove_finances' porque lo que se configura ahí es dinero (multa, precios, cobranza).
    { to: '/mi-estudio', label: 'Mi Estudio', icon: Building2, mod: 'letsmove_finances' },
    // WhatsApp (12-oct-2026): vincular el número del estudio por QR + salud + pausa. Gate por módulo
    // `whatsapp` (el mismo que exige el backend /api/messages/whatsapp*) Y solo dueña/gerente.
    { to: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, mod: 'whatsapp', adminOnly: true },
    // Ayuda (E7d, 31-ago-2026): bot del manual, sin gate de módulo — para todo el staff.
    // Mis peticiones (F2 peticiones, 11-oct-2026): lo que el estudio nos ha pedido, en qué va, y
    // responder cuando le pedimos un dato. Entrada PROPIA (no pestaña de Ayuda, pedido Carlos) y sin
    // gate de módulo, igual que en la agenda dental. Globo = peticiones «Esperando tu respuesta».
    { to: '/peticiones', label: 'Mis peticiones', icon: Ticket },
    { to: '/ayuda', label: 'Ayuda', icon: HelpCircle },
  ] },
];

// (El título de sección en el topbar se retiró el 18-ago-2026, pedido de Carlos: se veía
// TRES veces — topbar + encabezado propio de cada página + item iluminado del sidebar.
// Manda el encabezado de la página, que es el que trae conteo/vista/subtítulo/acciones.)

export default function AppLayout() {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Historial propio para que los «Volver» de las fichas sepan de dónde se llegó.
  useEffect(() => { recordNav(location.pathname + location.search); }, [location.pathname, location.search]);
  // Qué secciones se usan y quién (services/telemetry.ts): solo la ruta, sin la búsqueda.
  useEffect(() => { registrarVista(location.pathname); }, [location.pathname]);
  const [mobileOpen, setMobileOpen] = useState(false); // off-canvas (móvil)
  const [fabOpen, setFabOpen] = useState(false); // bottom-sheet del FAB (móvil)
  const [showProfile, setShowProfile] = useState(false); // Mi Perfil (módulo perfil compartido)
  const [myAvatar, setMyAvatar] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false); // dropdown del topbar
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Globo de «Mis peticiones»: cuántas esperan un dato del estudio. Se refresca cada 10 min y al
  // crear/responder una (evento del servicio). Si falla, simplemente no hay globo.
  // Quién la ve lo decide el SERVIDOR (`puede`): dueño/encargado por defecto y permiso por persona en
  // users.requests_access — en Let's Move solo Karla y Mabel (Carlos 11-oct-2026; cada petición le avisa por WA).
  const [puedePeticiones, setPuedePeticiones] = useState(false);
  const [petBadge, setPetBadge] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => getClientRequestsCount().then((c) => { if (alive) { setPuedePeticiones(c.puede !== false); setPetBadge(c.esperando || 0); } }).catch(() => { /* sin globo */ });
    load();
    const iv = setInterval(load, 600000);
    window.addEventListener(CLIENT_REQUESTS_EVENT, load);
    return () => { alive = false; clearInterval(iv); window.removeEventListener(CLIENT_REQUESTS_EVENT, load); };
  }, []);
  const badges: Record<string, number> = { '/peticiones': petBadge };

  useEffect(() => {
    let alive = true;
    getMyProfile().then((r) => { if (alive) setMyAvatar(r.profile.avatar_url); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Cierra el menú de usuario al hacer clic fuera.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  // Modo pantalla (screensaver de próximas clases): solo por inactividad (2 min).
  // El botón manual del topbar se retiró (decisión de Carlos 2026-07-01).
  const { active: showSaver, dismiss: dismissSaver } = useIdleScreenSaver(SCREENSAVER_IDLE_MS, true);

  async function doLogout() { await logout(); navigate('/login'); }

  const base = 'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition';

  const renderItem = ({ to, label, icon: Icon, ready }: NavItem, onNavigate?: () => void) => {
    if (ready === false) {
      return (
        <div key={label} className={`${base} cursor-default text-content-3 opacity-60`} title="Próximamente">
          <Icon size={20} className="shrink-0" />
          <span className="truncate">{label}</span>
          <span className="ml-auto rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-content-3">pronto</span>
        </div>
      );
    }
    return (
      <NavLink
        key={label}
        to={to}
        end={to === '/'}
        onClick={onNavigate}
        className={({ isActive }) =>
          `${base} ${isActive ? 'bg-accent font-semibold text-on-accent shadow-sm' : 'text-content-2 hover:bg-surface-2 hover:text-content'}`
        }
      >
        {({ isActive }) => (<>
          <Icon size={20} className="shrink-0" />
          <span className="truncate">{label}</span>
          {badges[to] > 0 && (
            // Activo: globo invertido (texto de marca sobre fondo «on-accent») para que no se pierda en el relleno del ítem.
            <span className={`ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${isActive ? 'bg-on-accent text-accent-solid' : 'bg-amber-500 text-white'}`}
              title={`${badges[to]} esperando tu respuesta`} aria-label={`${badges[to]} esperando tu respuesta`} data-testid={`nav-badge-${to.slice(1)}`}>
              {badges[to]}
            </span>
          )}
        </>)}
      </NavLink>
    );
  };

  const navLinks = (onNavigate?: () => void) =>
    SECTIONS.map((sec) => {
      // Oculta items cuyo módulo esté inactivo; si la sección queda vacía, no se pinta.
      const items = sec.items.filter((it) => (!it.mod || hasModule(it.mod)) && (!it.mods || it.mods.every(hasModule)) && (!it.adminOnly || user?.role === 'admin') && (it.to !== '/peticiones' || puedePeticiones));
      if (items.length === 0) return null;
      return (
        <div key={sec.title} className="space-y-1">
          <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-content-3">{sec.title}</div>
          {items.map((it) => renderItem(it, onNavigate))}
        </div>
      );
    });

  /** Contenido del sidebar reutilizado en escritorio y móvil. */
  const SidebarInner = ({ onNavigate }: { onNavigate?: () => void }) => (
    <div className="lm-sidebar relative flex h-full flex-col bg-surface">
      {/* Cabecera de marca (sin línea: separa el propio padding) */}
      <div className="lm-brand relative flex items-center gap-2 px-4 pb-1" style={{ height: 64 }}>
        <img src={logo} alt="Let's Move" className="h-10 w-10 rounded-full bg-white object-contain ring-2 ring-accent/50" />
        <span className="flex flex-col leading-none">
          <span className="font-display text-base font-extrabold tracking-wide text-content">LET'S MOVE</span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-content-3">Dance &amp; Fitness</span>
        </span>
      </div>

      {/* Navegación */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {navLinks(onNavigate)}
      </nav>

      {/* El perfil y Salir viven ahora en el menú de usuario del topbar (2026-07-01). */}
    </div>
  );

  return (
    <div className="flex h-full bg-canvas text-content">
      {/* ===== Sidebar escritorio: tarjeta flotante (patrón agenda Jesara, OK Carlos 28-jul) =====
          Sin border-r ni botón de colapsar: separa el contraste canvas/surface + sombra. */}
      <aside className="hidden w-60 shrink-0 p-3 pr-0 md:block">
        <div className="h-full overflow-hidden rounded-2xl bg-surface shadow-lg">
          <SidebarInner />
        </div>
      </aside>

      {/* ===== Off-canvas móvil ===== */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 overflow-hidden rounded-r-2xl shadow-xl">
            <SidebarInner onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* ===== Columna principal ===== */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar: transparente sobre el canvas — sin border-b ni fondo, para que no
            cruce ninguna línea con el sidebar (lo «cuadrado» que señaló Carlos 28-jul).
            z-30 se conserva para que el dropdown del usuario quede sobre el contenido. */}
        <header className="relative z-30 flex h-16 shrink-0 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-content-2 ring-1 ring-line hover:bg-surface-2 md:hidden"
              aria-label="Abrir menú"
            >
              <Menu size={18} />
            </button>
            {/* Hueco para el «Volver» de las fichas (BackButton lo llena por portal en
                escritorio). Así el botón no se come un renglón entero encima del contenido
                — pedido de Carlos 15-ago con la ficha de alumno. */}
            <div id="lm-topbar-back" className="hidden md:flex md:items-center" />
          </div>
          <div className="flex items-center gap-2">
            {hasModule('letsmove_members') && <GlobalSearch />}
            <HalloweenToggle />
            <ThemeToggle />

            {/* Menú de usuario (mismo patrón que la agenda dental) */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                title="Mi cuenta"
                className="flex items-center gap-1 rounded-full p-0.5 pr-1.5 ring-1 ring-line transition hover:bg-surface-2"
              >
                {myAvatar
                  ? <img src={myAvatar} alt="Mi foto" className="h-8 w-8 rounded-full object-cover" />
                  : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-solid text-xs font-bold text-on-accent">
                      {getInitials(user?.firstname, user?.lastname)}
                    </div>}
                <ChevronDown size={14} className={`text-content-3 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-line bg-surface py-2 shadow-xl">
                  <div className="flex items-center gap-2.5 border-b border-line px-4 py-3" title={user?.email}>
                    {myAvatar
                      ? <img src={myAvatar} alt="Mi foto" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                      : <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-solid text-xs font-bold text-on-accent">
                          {getInitials(user?.firstname, user?.lastname)}
                        </div>}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-content">
                        {[user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email}
                      </p>
                      <p className="truncate text-xs text-content-3">{user ? ROLE_LABEL[user.role] ?? user.role : ''}</p>
                    </div>
                  </div>
                  <div className="p-2">
                    {hasModule('perfil') && (
                      <button
                        onClick={() => { setUserMenuOpen(false); setShowProfile(true); }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-content-2 transition hover:bg-surface-2 hover:text-content"
                      >
                        <User size={16} /> Mi Perfil
                      </button>
                    )}
                    <button
                      onClick={() => { setUserMenuOpen(false); doLogout(); }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-500 transition hover:bg-red-500/10"
                    >
                      <LogOut size={16} /> Salir
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Contenido */}
        <main className="flex-1 overflow-y-auto p-4 pb-[calc(4.75rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-6">
          {/* Ancho adaptativo (OK Carlos 28-jul): páginas DENSAS (calendario/tablas/reportes)
              fluyen hasta 1600px; fichas y formularios se quedan en 6xl por legibilidad. */}
          <div className={`mx-auto ${isDense(location.pathname) ? 'max-w-[1600px]' : 'max-w-6xl'} animate-fade-in-up`}>
            <PlanBanner />
            <Outlet />
          </div>
        </main>
      </div>

      {/* ===== Barra inferior fija (solo móvil) — navegación tipo app ===== */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex items-stretch border-t border-line bg-surface/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        {([
          { to: '/', label: 'Inicio', icon: LayoutDashboard, end: true },
          { to: '/alumnos', label: 'Alumnos', icon: Users, mod: 'letsmove_members' },
        ] as { to: string; label: string; icon: typeof Users; end?: boolean; mod?: string }[])
          .filter((i) => !i.mod || hasModule(i.mod))
          .map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition ${
                isActive ? 'text-accent-ink' : 'text-content-3 hover:text-content-2'
              }`
            }
          >
            <Icon size={20} /> {label}
          </NavLink>
        ))}

        {/* FAB ＋ central, elevado */}
        <div className="flex flex-1 items-start justify-center">
          <button
            onClick={() => setFabOpen(true)}
            aria-label="Acción rápida"
            className="-mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-on-accent shadow-lg ring-4 ring-surface transition active:scale-95"
          >
            <Plus size={24} />
          </button>
        </div>

        {([
          { to: '/finanzas', label: 'Finanzas', icon: Wallet, mod: 'letsmove_finances' },
        ] as { to: string; label: string; icon: typeof Users; mod?: string }[])
          .filter((i) => !i.mod || hasModule(i.mod))
          .map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition ${
                isActive ? 'text-accent-ink' : 'text-content-3 hover:text-content-2'
              }`
            }
          >
            <Icon size={20} /> {label}
          </NavLink>
        ))}
        <button
          onClick={() => setMobileOpen(true)}
          className="relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium text-content-3 transition hover:text-content-2"
          aria-label={petBadge > 0 ? `Más (${petBadge} peticiones esperan tu respuesta)` : undefined}
        >
          <MoreHorizontal size={20} /> Más
          {/* Punto: hay peticiones esperando respuesta dentro del menú «Más» */}
          {petBadge > 0 && <span className="absolute right-[calc(50%-16px)] top-1.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-surface" data-testid="mas-dot" />}
        </button>
      </nav>

      {/* Bottom-sheet del FAB: acciones rápidas */}
      {fabOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setFabOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-line bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" />
            <p className="mb-3 px-1 text-sm font-semibold text-content">¿Qué quieres hacer?</p>
            <div className="space-y-2">
              {hasModule('letsmove_finances') && (
                <button
                  onClick={() => { setFabOpen(false); navigate('/caja?t=ingresos&nuevo=pago'); }}
                  className="flex w-full items-center gap-3 rounded-xl bg-surface-2 px-4 py-3 text-left text-sm font-medium text-content transition active:scale-[0.99]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><DollarSign size={18} /></span>
                  Registrar pago
                </button>
              )}
              {hasModule('letsmove_members') && (
                <button
                  onClick={() => { setFabOpen(false); navigate('/alumnos?nuevo=1'); }}
                  className="flex w-full items-center gap-3 rounded-xl bg-surface-2 px-4 py-3 text-left text-sm font-medium text-content transition active:scale-[0.99]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-accent-ink"><UserPlus size={18} /></span>
                  Nuevo alumno
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* cierre del off-canvas con tecla — accesible vía botón X flotante en móvil */}
      {mobileOpen && (
        <button
          onClick={() => setMobileOpen(false)}
          className="fixed right-4 top-4 z-50 flex h-9 w-9 items-center justify-center rounded-lg bg-surface text-content ring-1 ring-line md:hidden"
          aria-label="Cerrar menú"
        >
          <X size={18} />
        </button>
      )}

      {/* Modo pantalla (screensaver de próximas clases) — tras inactividad o botón */}
      {showSaver && (
        <Suspense fallback={null}>
          <ScreenSaverMode onDismiss={dismissSaver} />
        </Suspense>
      )}

      <MyProfileModal open={showProfile} onClose={() => setShowProfile(false)} onUpdated={(p) => setMyAvatar(p.avatar_url)} />
      <ImpersonationBanner />
    </div>
  );
}
